
import React, { useState, useEffect } from 'react';
import { UserProfile, CircleType, CircleItem, SobrietyDate, StepProgress } from '../types';
import { Plus, Trash2, Check, User, Info, Mail, AlertCircle, ShieldCheck, Share2, MapPin, Phone, Moon, Sun, ChevronDown, HelpCircle, Footprints, Calendar, Clock, RefreshCw, Database } from 'lucide-react';
import { useLocation } from 'react-router-dom';

interface ProfilePageProps {
  profile: UserProfile;
  onUpdate: (profile: UserProfile) => void;
  isDark: boolean;
  toggleTheme: () => void;
}

// RESTORATION DATA FOR DEMO TOGGLE
const DEMO_RESTORE_DATA: Partial<UserProfile> = {
  circleItems: [
    { id: '1', text: 'Acting out sexually', type: CircleType.INNER },
    { id: '2', text: 'Isolation', type: CircleType.INNER },
    { id: '3', text: 'Late night internet', type: CircleType.MIDDLE },
    { id: '4', text: 'Resentment towards boss', type: CircleType.MIDDLE },
    { id: '5', text: 'Daily Meditation', type: CircleType.OUTER },
    { id: '6', text: 'Calling Sponsor', type: CircleType.OUTER },
  ],
  serviceHistory: [
    { id: '1', date: new Date().toISOString(), action: 'Made coffee at 7am meeting', category: 'meeting' },
    { id: '2', date: new Date(Date.now() - 86400000).toISOString(), action: 'Called a newcomer', category: 'call' }
  ],
  meetingHistory: [
      { id: '1', date: new Date().toISOString(), type: 'AA', name: 'Sunrise Serenity', online: false },
      { id: '2', date: new Date(Date.now() - 86400000 * 2).toISOString(), type: 'AA', name: 'Friday Night Lights', online: false }
  ],
  reviewHistory: [
      {
          id: 'mock_1',
          date: new Date(Date.now() - 86400000 * 2).toISOString(),
          answers: {},
          aiResponse: { 
              insight: 'Good focus.', 
              reflection: 'Keep going.', 
              action: 'Pray.', 
              isHighRisk: false, 
              sentimentScore: 7,
              defectScores: { resentment: 2, selfishness: 3, dishonesty: 0, fear: 5 }
          }
      },
      {
          id: 'mock_2',
          date: new Date(Date.now() - 86400000 * 1).toISOString(),
          answers: {},
          aiResponse: { 
              insight: 'Struggling with self-will.', 
              reflection: 'Review page 86.', 
              action: 'Call sponsor.', 
              isHighRisk: false, 
              sentimentScore: 4,
              defectScores: { resentment: 8, selfishness: 6, dishonesty: 1, fear: 4 } 
          }
      }
  ],
  proteges: [
    { id: '1', name: 'Dave K.', status: 'green', lastCheckIn: '2 hours ago', step: 10, pendingReview: false },
    { id: '2', name: 'Sarah M.', status: 'yellow', lastCheckIn: '2 days ago', step: 4, pendingReview: true }, 
    { id: '3', name: 'Mike T.', status: 'red', lastCheckIn: '5 days ago', step: 1, pendingReview: false },
  ],
  stepProgress: [
      { stepNumber: 1, status: 'completed', homeworkCompleted: true, feedbackHistory: [{ id: '1', date: new Date().toISOString(), sender: 'sponsor', text: 'Welcome to the journey.' }] },
      { stepNumber: 2, status: 'completed', homeworkCompleted: true, feedbackHistory: [] },
      { stepNumber: 3, status: 'completed', homeworkCompleted: true, feedbackHistory: [] },
      { stepNumber: 4, status: 'active', homeworkCompleted: false, assignedHomework: "Complete at least 5 columns in the Inventory Tool and read p.64-71.", dueDate: new Date(Date.now() + 86400000 * 2).toISOString(), feedbackHistory: [] },
      { stepNumber: 5, status: 'locked', homeworkCompleted: false, feedbackHistory: [] },
      { stepNumber: 6, status: 'locked', homeworkCompleted: false, feedbackHistory: [] },
      { stepNumber: 7, status: 'locked', homeworkCompleted: false, feedbackHistory: [] },
      { stepNumber: 8, status: 'locked', homeworkCompleted: false, feedbackHistory: [] },
      { stepNumber: 9, status: 'locked', homeworkCompleted: false, feedbackHistory: [] },
      { stepNumber: 10, status: 'locked', homeworkCompleted: false, feedbackHistory: [] },
      { stepNumber: 11, status: 'locked', homeworkCompleted: false, feedbackHistory: [] },
      { stepNumber: 12, status: 'locked', homeworkCompleted: false, feedbackHistory: [] },
  ]
};

export const ProfilePage: React.FC<ProfilePageProps> = ({ profile, onUpdate, isDark, toggleTheme }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'general' | 'sobriety' | 'circles' | 'questions' | 'sharing'>('general');
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  // Check for incoming state from other components (like ThreeCircles)
  useEffect(() => {
    if (location.state && (location.state as any).activeTab) {
        setActiveTab((location.state as any).activeTab);
    }
  }, [location]);

  // Direct Update Handler (Auto-Save)
  const handleChange = (field: keyof UserProfile, value: any) => {
    onUpdate({ ...profile, [field]: value });
  };

  const handleShareToggle = (key: keyof UserProfile['sharingConfig']) => {
      const newConfig = { ...profile.sharingConfig, [key]: !profile.sharingConfig[key] };
      handleChange('sharingConfig', newConfig);
  }

  // Helper for input styles
  const inputClass = "w-full p-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white transition-colors text-sm";
  const labelClass = "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1";

  // Three Circles Logic
  const addCircleItem = (text: string, type: CircleType) => {
    if(!text.trim()) return;
    const newItem: CircleItem = { id: Date.now().toString(), text, type };
    handleChange('circleItems', [...profile.circleItems, newItem]);
  };

  const removeCircleItem = (id: string) => {
    handleChange('circleItems', profile.circleItems.filter(i => i.id !== id));
  };

  // Custom Questions Logic
  const addQuestion = (text: string) => {
      if(!text.trim()) return;
      const newQ = { id: Date.now().toString(), text, isDefault: false };
      handleChange('customQuestions', [...profile.customQuestions, newQ]);
  };

  const removeQuestion = (id: string) => {
      handleChange('customQuestions', profile.customQuestions.filter(q => q.id !== id));
  };

  // Sobriety Dates Logic
  const addSobrietyDate = () => {
    const newDate: SobrietyDate = {
        id: Date.now().toString(),
        label: 'New Tracker',
        definition: 'Abstinence from...',
        date: new Date().toISOString().split('T')[0]
    };
    handleChange('sobrietyDates', [...profile.sobrietyDates, newDate]);
  };

  const updateSobrietyDate = (id: string, field: keyof SobrietyDate, value: string) => {
      const updated = profile.sobrietyDates.map(d => d.id === id ? { ...d, [field]: value } : d);
      handleChange('sobrietyDates', updated);
  };

  const removeSobrietyDate = (id: string) => {
      if (profile.sobrietyDates.length <= 1) {
          alert("You must have at least one sobriety date.");
          return;
      }
      handleChange('sobrietyDates', profile.sobrietyDates.filter(d => d.id !== id));
  };

  const handleEmailChange = (newEmail: string) => {
      handleChange('email', newEmail);
      handleChange('emailVerified', false); // Require re-verification
  };

  // Logic to determine current active step for the dropdown
  const getCurrentStepValue = () => {
    const active = profile.stepProgress.find(s => s.status === 'active' || s.status === 'pending');
    if (active) return active.stepNumber;
    
    const allCompleted = profile.stepProgress.every(s => s.status === 'completed');
    if (allCompleted) return 'ALL';
    
    return 1; // Default fallback
  };

  const handleStepReset = (value: string) => {
      const val = value === 'ALL' ? 'ALL' : parseInt(value);
      
      const newProgress = Array.from({ length: 12 }, (_, i) => {
        const stepNum = i + 1;
        let status: any = 'locked';
        let hw = false;
        
        if (val === 'ALL') {
            status = 'completed';
            hw = true;
        } else {
            if (stepNum < val) {
                status = 'completed';
                hw = true;
            } else if (stepNum === val) {
                status = 'active';
                hw = false;
            } else {
                status = 'locked';
                hw = false;
            }
        }

        return {
            stepNumber: stepNum,
            status: status,
            homeworkCompleted: hw,
            feedbackHistory: [] // Clean history on reset
        } as StepProgress;
      });

      handleChange('stepProgress', newProgress);
  };

  // DEMO TOGGLE FUNCTION
  const handleDemoToggle = () => {
      // Simple heuristic: If circle items are empty, we assume it's in "Empty State"
      const isEmptyState = profile.circleItems.length === 0;

      if (isEmptyState) {
          // Restore Demo Data
          onUpdate({
              ...profile,
              ...DEMO_RESTORE_DATA
          });
      } else {
          // Switch to Empty State
          onUpdate({
              ...profile,
              circleItems: [],
              meetingHistory: [],
              reviewHistory: [],
              serviceHistory: [],
              proteges: [], // CLEAR SPONSOR DATA
              stepProgress: [] // Triggers useEffect in StepsPath to init default empty steps
          });
      }
  };

  return (
    <div className="space-y-4 md:space-y-6 w-full max-w-full">
      <div className="flex justify-between items-center">
        <div>
            <h2 className="text-xl md:text-2xl font-bold dark:text-white">Profile & Settings</h2>
            <p className="text-xs md:text-sm text-slate-500 flex items-center gap-1 mt-1">
                <Check className="w-3 h-3 text-green-500" /> Changes saved automatically
            </p>
        </div>
      </div>

      {/* MOBILE: Dropdown Menu for Navigation */}
      <div className="md:hidden">
        <label htmlFor="mobile-tab-select" className="sr-only">Select Section</label>
        <div className="relative">
            <select
                id="mobile-tab-select"
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value as any)}
                className="w-full appearance-none p-3 pl-4 pr-10 rounded-xl border border-slate-300 bg-white text-slate-900 font-medium focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white shadow-sm transition-shadow"
            >
                <option value="general">General Info</option>
                <option value="sobriety">Sobriety</option>
                <option value="circles">Three Circles</option>
                <option value="questions">Nightly Review</option>
                <option value="sharing">Permissions</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500 dark:text-slate-400">
                <ChevronDown className="h-5 w-5" />
            </div>
        </div>
      </div>

      {/* DESKTOP: Horizontal Tabs */}
      <div className="hidden md:flex border-b border-slate-200 dark:border-slate-700">
            <button
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'general' 
                ? 'border-brand-600 text-brand-600 dark:text-brand-400' 
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
            >
            General Info
            </button>
            <button
            onClick={() => setActiveTab('sobriety')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'sobriety' 
                ? 'border-brand-600 text-brand-600 dark:text-brand-400' 
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
            >
            Sobriety
            </button>
            <button
            onClick={() => setActiveTab('circles')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'circles' 
                ? 'border-brand-600 text-brand-600 dark:text-brand-400' 
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
            >
            Three Circles
            </button>
            <button
            onClick={() => setActiveTab('questions')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'questions' 
                ? 'border-brand-600 text-brand-600 dark:text-brand-400' 
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
            >
            Nightly Review
            </button>
            <button
            onClick={() => setActiveTab('sharing')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'sharing' 
                ? 'border-brand-600 text-brand-600 dark:text-brand-400' 
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
            >
            <Share2 className="w-4 h-4" /> Permissions
            </button>
      </div>

      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 md:p-6 border border-slate-200 dark:border-slate-700">
        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="space-y-6 md:space-y-8 max-w-3xl">
            {/* Appearance Section */}
            <section className="flex items-center justify-between p-3 md:p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
                <div className="flex items-center gap-3">
                   <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-700 text-yellow-400' : 'bg-orange-100 text-orange-500'}`}>
                       {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                   </div>
                   <div>
                       <h3 className="text-sm font-bold text-slate-900 dark:text-white">Appearance</h3>
                       <p className="text-xs text-slate-500">{isDark ? 'Dark Mode Active' : 'Light Mode Active'}</p>
                   </div>
                </div>
                <button 
                    onClick={toggleTheme}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors bg-slate-200 dark:bg-slate-600"
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${isDark ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
            </section>

            {/* Identity */}
            <section>
                <h3 className="flex items-center gap-2 font-bold dark:text-white mb-4">
                    <User className="w-4 h-4" /> Identity
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>First Name <span className="text-red-500">*</span></label>
                        <input 
                        type="text" 
                        value={profile.firstName}
                        onChange={e => handleChange('firstName', e.target.value)}
                        className={inputClass}
                        placeholder="e.g. Bill"
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Last Initial <span className="text-red-500">*</span></label>
                        <input 
                        type="text" 
                        value={profile.lastInitial}
                        onChange={e => handleChange('lastInitial', e.target.value)}
                        className={inputClass}
                        maxLength={1}
                        placeholder="W"
                        />
                    </div>
                    
                    {/* Email Section */}
                    <div className="col-span-1 md:col-span-2 w-full min-w-0">
                         <label className={labelClass}>Email Address <span className="text-red-500">*</span></label>
                         
                         <div className="flex flex-col gap-2">
                            {isChangingEmail ? (
                                <div className="flex gap-2">
                                    <input 
                                        type="email" 
                                        value={profile.email}
                                        onChange={e => handleEmailChange(e.target.value)}
                                        className={inputClass}
                                        autoFocus
                                    />
                                    <button 
                                        onClick={() => setIsChangingEmail(false)}
                                        className="bg-brand-600 text-white px-4 rounded-lg text-sm font-medium hover:bg-brand-700 whitespace-nowrap"
                                    >
                                        Done
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-wrap sm:flex-nowrap justify-between items-center p-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-900 gap-2 w-full">
                                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300 overflow-hidden min-w-0 flex-1">
                                        <Mail className="w-4 h-4 flex-shrink-0" />
                                        <span className="truncate text-sm">{profile.email}</span>
                                        {profile.emailVerified ? (
                                            <span className="flex-shrink-0 flex items-center gap-1 text-[10px] bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-bold">
                                                <ShieldCheck className="w-3 h-3" />
                                                <span className="hidden sm:inline">Verified</span>
                                            </span>
                                        ) : (
                                            <span className="flex-shrink-0 flex items-center gap-1 text-[10px] bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full font-bold">
                                                <AlertCircle className="w-3 h-3" /> 
                                                <span className="hidden sm:inline">Unverified</span>
                                            </span>
                                        )}
                                    </div>
                                    <button 
                                        onClick={() => setIsChangingEmail(true)}
                                        className="text-xs sm:text-sm text-brand-600 hover:underline font-medium whitespace-nowrap ml-auto"
                                    >
                                        Change
                                    </button>
                                </div>
                            )}
                         </div>
                    </div>

                    {/* Phone Number */}
                    <div className="col-span-1 md:col-span-2">
                        <label className={labelClass}>
                            Phone Number <span className="text-slate-400 font-normal">(Optional)</span>
                        </label>
                        <div className="relative">
                            <Phone className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                            <input 
                                type="tel" 
                                value={profile.phone}
                                onChange={e => handleChange('phone', e.target.value)}
                                className={`${inputClass} pl-10`}
                                placeholder="(555) 123-4567"
                            />
                        </div>
                        <p className="text-[10px] sm:text-xs text-slate-500 mt-1">Required if you wish to use SMS features with your sponsor.</p>
                    </div>
                </div>
            </section>

             {/* Location Section */}
             <section className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <h3 className="flex items-center gap-2 font-bold dark:text-white mb-2">
                    <MapPin className="w-4 h-4" /> My Location
                </h3>
                <p className="text-sm text-slate-500 mb-4">Used to locate nearby meetings via "The Bridge" finder.</p>
                
                <div className="space-y-2">
                    <label className={labelClass}>Your Location</label>
                    <div className="relative">
                        <input 
                            type="text" 
                            value={profile.location || ''}
                            onChange={e => handleChange('location', e.target.value)}
                            className={inputClass}
                            placeholder="e.g. 'Akron, OH', 'Paris, France', or '90210'"
                        />
                         <div className="absolute right-3 top-3 text-slate-400">
                             <MapPin className="w-4 h-4" />
                         </div>
                    </div>
                    
                    {/* Tooltip / Helper Text */}
                    <div className="flex items-start gap-2 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800">
                        <HelpCircle className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-indigo-700 dark:text-indigo-300">
                            This field is flexible! You can enter a <b>Zip Code</b>, a <b>City & State</b>, or even a <b>Country</b> (e.g. "London, UK").
                        </p>
                    </div>
                </div>
            </section>

             {/* Recovery Progress Baseline */}
            <section className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <h3 className="flex items-center gap-2 font-bold dark:text-white mb-2">
                    <Footprints className="w-4 h-4" /> Recovery Stage
                </h3>
                <p className="text-sm text-slate-500 mb-4">Set your current position in the 12 Steps. This helps us tailor your journey.</p>
                
                <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                    <label className={labelClass}>Current Status</label>
                    <select
                        value={getCurrentStepValue()}
                        onChange={(e) => handleStepReset(e.target.value)}
                        className={inputClass}
                    >
                        <option value="1">Step 1: Honesty</option>
                        <option value="2">Step 2: Hope</option>
                        <option value="3">Step 3: Surrender</option>
                        <option value="4">Step 4: Inventory</option>
                        <option value="5">Step 5: Integrity</option>
                        <option value="6">Step 6: Willingness</option>
                        <option value="7">Step 7: Humility</option>
                        <option value="8">Step 8: Love</option>
                        <option value="9">Step 9: Justice</option>
                        <option value="10">Step 10: Perseverance</option>
                        <option value="11">Step 11: Spirituality</option>
                        <option value="12">Step 12: Service</option>
                        <option value="ALL">I have completed all 12 Steps (Maintenance)</option>
                    </select>
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Changing this will reset your current step timeline and clear feedback history.
                    </p>
                </div>
            </section>
            
            {/* DEMO TOGGLE */}
            <section className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <button 
                    onClick={handleDemoToggle}
                    className="w-full py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-2"
                >
                    <Database className="w-3 h-3" /> Toggle Demo / Empty State (Dev)
                </button>
            </section>
          </div>
        )}

        {/* Sobriety Tab */}
        {activeTab === 'sobriety' && (
             <div className="max-w-3xl">
                 <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-lg font-bold dark:text-white flex items-center gap-2">
                            <Clock className="w-5 h-5 text-brand-500" /> Sobriety Tracking
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">Define your clean dates and what they mean to you. You can track multiple substances or behaviors.</p>
                    </div>
                    <button 
                        onClick={addSobrietyDate}
                        className="text-sm bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 shadow-sm"
                    >
                        <Plus className="w-4 h-4" /> Add Tracker
                    </button>
                </div>

                <div className="space-y-4">
                    {profile.sobrietyDates.map((dateItem, idx) => (
                        <div key={dateItem.id} className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm relative group">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className={labelClass}>Label <span className="text-xs font-normal text-slate-400">(e.g. Alcohol, Nicotine)</span></label>
                                        <input 
                                            value={dateItem.label}
                                            onChange={(e) => updateSobrietyDate(dateItem.id, 'label', e.target.value)}
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Definition <span className="text-xs font-normal text-slate-400">(Your personal boundary)</span></label>
                                        <textarea 
                                            value={dateItem.definition}
                                            onChange={(e) => updateSobrietyDate(dateItem.id, 'definition', e.target.value)}
                                            className={`${inputClass} resize-none h-24`}
                                            placeholder="e.g. Total abstinence from all mind-altering substances."
                                        />
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className={labelClass}>Clean Date</label>
                                        <div className="relative">
                                            <input 
                                                type="date"
                                                value={dateItem.date}
                                                onChange={(e) => updateSobrietyDate(dateItem.id, 'date', e.target.value)}
                                                className={inputClass}
                                            />
                                            <div className="absolute right-3 top-3 text-slate-400 pointer-events-none">
                                                <Calendar className="w-4 h-4" />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Calculated Time Display Helper */}
                                    <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-100 dark:border-slate-700 text-center">
                                         <span className="text-xs text-slate-500 uppercase tracking-wider font-bold">Current Streak</span>
                                         <div className="text-2xl font-black text-brand-600 dark:text-brand-400 mt-1">
                                             {Math.floor((new Date().getTime() - new Date(dateItem.date).getTime()) / (1000 * 60 * 60 * 24))} Days
                                         </div>
                                    </div>

                                    {idx > 0 && (
                                        <div className="flex justify-end pt-2">
                                            <button 
                                                onClick={() => removeSobrietyDate(dateItem.id)}
                                                className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 font-medium bg-red-50 dark:bg-red-900/10 px-3 py-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                            >
                                                <Trash2 className="w-3 h-3" /> Delete Tracker
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
             </div>
        )}

        {/* Circles Tab */}
        {activeTab === 'circles' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {/* Reordered: Outer (Green) -> Middle (Yellow) -> Inner (Red) */}
             {[CircleType.OUTER, CircleType.MIDDLE, CircleType.INNER].map((type) => (
                 <div key={type} className={`border-t-4 rounded-lg p-4 bg-white dark:bg-slate-800 shadow-sm ${
                    type === CircleType.INNER ? 'border-red-500' :
                    type === CircleType.MIDDLE ? 'border-amber-400' :
                    'border-green-500'
                 }`}>
                     <h3 className={`font-bold mb-1 uppercase tracking-wider text-xs ${
                         type === CircleType.INNER ? 'text-red-600 dark:text-red-400' :
                         type === CircleType.MIDDLE ? 'text-amber-600 dark:text-amber-400' :
                         'text-green-600 dark:text-green-400'
                     }`}>
                         {type === CircleType.INNER ? 'Inner Circle' : type === CircleType.MIDDLE ? 'Middle Circle' : 'Outer Circle'}
                     </h3>
                     <p className="text-xs text-slate-400 mb-4">
                         {type === CircleType.INNER ? 'Behaviors that mean I have lost my sobriety.' : 
                          type === CircleType.MIDDLE ? 'Slippery slope behaviors.' : 
                          'Healthy behaviors to cultivate.'}
                     </p>
                     
                     <div className="flex gap-2 mb-3">
                        <input 
                            id={`input-${type}`}
                            placeholder="Add behavior..."
                            className="w-full text-xs p-2 rounded border border-slate-300 bg-white text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-500"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    addCircleItem(e.currentTarget.value, type);
                                    e.currentTarget.value = '';
                                }
                            }}
                        />
                     </div>

                     <ul className="space-y-1">
                        {profile.circleItems.filter(i => i.type === type).map(item => (
                            <li key={item.id} className="flex justify-between items-center group p-1 hover:bg-slate-50 dark:hover:bg-slate-700 rounded">
                                <span className="text-sm text-slate-700 dark:text-slate-200">{item.text}</span>
                                <button onClick={() => removeCircleItem(item.id)} className="text-slate-300 hover:text-red-500 md:opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </li>
                        ))}
                     </ul>
                 </div>
             ))}
          </div>
        )}

        {/* Questions Tab */}
        {activeTab === 'questions' && (
            <div className="max-w-2xl">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg flex gap-3 mb-6">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                        These questions appear in your Nightly Review. The original 5 questions are foundational to the Step 11 process and cannot be removed, but you may append your own custom questions.
                    </p>
                </div>
                
                <div className="space-y-3">
                    {profile.customQuestions.map((q) => (
                        <div key={q.id} className="flex gap-3 items-center p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                            <span className={`flex-1 text-sm ${q.isDefault ? 'font-medium text-slate-700 dark:text-slate-300' : 'text-slate-600 dark:text-slate-400'}`}>
                                {q.text}
                            </span>
                            {q.isDefault ? (
                                <span className="text-[10px] uppercase font-bold tracking-wider bg-slate-100 dark:bg-slate-700 text-slate-500 px-2 py-1 rounded">Default</span>
                            ) : (
                                <button onClick={() => removeQuestion(q.id)} className="text-slate-400 hover:text-red-500 p-1">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-6 flex gap-2">
                    <input 
                        id="new-question"
                        placeholder="Add a custom question..."
                        className={inputClass}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                const input = e.currentTarget;
                                addQuestion(input.value);
                                input.value = '';
                            }
                        }}
                    />
                    <button 
                        onClick={() => {
                            const input = document.getElementById('new-question') as HTMLInputElement;
                            if (input.value) {
                                addQuestion(input.value);
                                input.value = '';
                            }
                        }}
                        className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg font-medium whitespace-nowrap"
                    >
                        Add
                    </button>
                </div>
            </div>
        )}
        
        {/* Sharing Tab */}
        {activeTab === 'sharing' && (
            <div className="max-w-3xl">
                <div className="mb-6">
                     <h3 className="text-lg font-bold dark:text-white flex items-center gap-2">
                         <ShieldCheck className="w-5 h-5 text-indigo-500" /> Sponsor Bridge Permissions
                     </h3>
                     <p className="text-sm text-slate-500">
                         Control exactly what data is visible to your linked sponsor. You retain full ownership of your data at all times.
                     </p>
                </div>

                <div className="space-y-4">
                    {/* Permission Items */}
                    {[
                        { key: 'shareSobrietyDates', title: 'Share Sobriety Dates', desc: 'Allow sponsor to view your clean dates.' },
                        { key: 'shareInventory', title: 'Share Step 4 Inventory', desc: 'Allow sponsor to view resentment lists.' },
                        { key: 'shareReviews', title: 'Share Nightly Reviews', desc: 'Allow sponsor to read Step 11 responses.' },
                        { key: 'shareService', title: 'Share Service Ledger', desc: 'Allow sponsor to see service history.' },
                        { key: 'shareCircles', title: 'Share Three Circles', desc: 'Allow sponsor to view inner circle definitions.' },
                    ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
                            <div className="pr-4">
                                <h4 className="font-medium text-slate-900 dark:text-white text-sm">{item.title}</h4>
                                <p className="text-xs text-slate-500">{item.desc}</p>
                            </div>
                            <button 
                                onClick={() => handleShareToggle(item.key as keyof UserProfile['sharingConfig'])}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${profile.sharingConfig[item.key as keyof UserProfile['sharingConfig']] ? 'bg-brand-600' : 'bg-slate-200 dark:bg-slate-700'}`}
                            >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${profile.sharingConfig[item.key as keyof UserProfile['sharingConfig']] ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        )}
      </div>
    </div>
  );
};
